import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { fetchPresentationWorkspace, deletePresentation } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';

// Customer Presentations — unmodified appGetPresentationWorkspace(). An
// in-person sales tool: package/option selection with room measurements,
// converting selected options straight into an Estimate draft.
export function PresentationsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['presentationWorkspace'], queryFn: fetchPresentationWorkspace });

  const deleteMutation = useMutation({
    mutationFn: (presentationId: string) => deletePresentation(presentationId),
    onSuccess: (result) => result.ok && void queryClient.invalidateQueries({ queryKey: ['presentationWorkspace'] }),
  });

  if (query.isLoading) return <LoadingState label="Loading presentations…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load presentations.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load presentations.'} onRetry={() => query.refetch()} />;
  }

  const sessions = query.data.sessions ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Sales"
        title="Presentations"
        actions={
          <Link to="/presentations/builder">
            <Button>
              <Plus className="h-4 w-4" aria-hidden="true" />
              New Presentation
            </Button>
          </Link>
        }
      />

      {sessions.length === 0 && <EmptyState>No presentations yet.</EmptyState>}
      {sessions.length > 0 && (
        <ul className="space-y-2">
          {sessions.map((s) => (
            <li key={s.presentationId} className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink-surface p-3.5 shadow-card">
              <Link to={`/presentations/builder?presentationId=${encodeURIComponent(s.presentationId)}`} className="min-w-0 flex-1">
                <p className="truncate font-semibold text-white">{s.presentationTitle || s.project}</p>
                <p className="truncate text-sm text-slate-400">
                  {s.customerName} · {s.status} · {s.selectedTotalText}
                </p>
              </Link>
              <button
                type="button"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(s.presentationId)}
                className="shrink-0 text-xs font-semibold text-red-300 hover:text-red-200 disabled:opacity-60"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
